import React, { Fragment, useState, useEffect, useCallback, useMemo, useRef } from "react";
import moment from "moment";
import useTranslation from "Hooks/useTranslation";
import { useSelector, useDispatch } from "react-redux";
import { setPageTitle } from "Store/modules/mms/actions";
import { useAuth } from "Contexts/AuthProvider";
import { setPendingMainDashboardJob } from "storage/dashboard-storage";
import { useConfirm } from "Contexts/ConfirmContext";
import DmsService from "Services/DMS/dms-api.service";
import MyIcons from "assets/img/Icons/MyIcons";
import FilterListIcon from "@material-ui/icons/FilterList";
import {
  Badge,
  Button,
  Checkbox,
  CircularProgress,
  Fade,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Popover,
  TextField,
} from "@material-ui/core";
import { toast } from "react-toastify";
import debounce from "lodash.debounce";
import { resolveFileUrl, downloadFileFromUrl } from "helpers";

const RemoveIcon = () => <span className="removeIcon" />;

const normalizeCatalogResponse = (data) => {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  if (Array.isArray(data.items)) return data.items;
  if (Array.isArray(data.data)) return data.data;
  return [];
};

const parseRowFilesJson = (filesJson) => {
  if (!filesJson) return [];
  try {
    const parsed = JSON.parse(filesJson);
    if (!Array.isArray(parsed)) return [];
    // API may return PascalCase (Name/Url) or camelCase (name/url)
    return parsed.map((file) => ({
      name: file?.name ?? file?.Name ?? "",
      url: file?.url ?? file?.Url ?? "",
    }));
  } catch {
    return [];
  }
};

const createInlineRow = () => ({
  id: `new-${Date.now()}`,
  clientId: "",
  fleetId: "",
  padId: "",
  wellId: "",
  filesJson: null,
  pendingFiles: [],
});

const hasInlineRowData = (row) =>
  Boolean(row.clientId) ||
  Boolean(row.fleetId) ||
  Boolean(row.padId) ||
  Boolean(row.wellId) ||
  (row.pendingFiles?.length ?? 0) > 0 ||
  parseRowFilesJson(row.filesJson).length > 0;

const isInlineRowComplete = (row) => Boolean(row.padId);

const formatWellStagesInParens = (well) => {
  if (well?.stages == null || String(well.stages).trim() === "") return "";
  return ` (${well.stages})`;
};

const formatPadWellSelectionLabel = (padId, wellId, allPads, allWells) => {
  if (!padId) return "";
  const padName = allPads.find((p) => p.id === padId)?.name || "—";
  if (!wellId) return padName;
  const well = allWells.find((w) => w.id === wellId);
  const wellName = well?.name || "—";
  return `${padName}; ${wellName}${formatWellStagesInParens(well)}`;
};

const buildJobName = (padId, wellId, allPads, allWells) =>
  formatPadWellSelectionLabel(padId, wellId, allPads, allWells);

const JOB_PLANNER_CELL_DISPLAY_CLASS =
  "jobConfigurationCellValue jobConfigurationCellValueEditable";

const buildUpsertPayload = (row, allPads, allWells) => {
  const jobName = buildJobName(row.padId, row.wellId, allPads, allWells);
  const payload = {
    name: jobName || row.name || "",
    clientId: row.clientId || null,
    fleetId: row.fleetId || null,
    padId: row.padId,
    wellId: row.wellId || null,
    parametersJson: row.parametersJson ?? null,
    filesJson: row.filesJson ?? null,
  };
  if (row.id && !String(row.id).startsWith("new-")) {
    payload.id = row.id;
  }
  return payload;
};

const normalizeFilterList = (data) => {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  if (Array.isArray(data.items)) return data.items;
  if (Array.isArray(data.data)) return data.data;
  return [];
};

const normalizeLicenseFilterOptions = (data, licensesNames) => (
  normalizeFilterList(data)
    .map((item) => {
      if (item == null) return null;

      if (typeof item === "string" || typeof item === "number") {
        const licenseInfo = licensesNames?.find((ln) => ln.licenseId === item);
        return {
          value: item,
          label: licenseInfo?.name || String(item),
        };
      }

      const value = item.id ?? item.licenseId ?? item.value;
      const label = item.name ?? item.license ?? item.label ?? String(value ?? "");
      return value == null ? null : { value, label };
    })
    .filter(Boolean)
);

const normalizeStageFilterOptions = (data) => (
  normalizeFilterList(data)
    .map((item) => {
      if (item == null || item === "") return null;

      const value = typeof item === "object"
        ? (item.stage ?? item.jobsPlannerStage ?? item.value ?? item.name)
        : item;

      if (value == null || value === "") return null;
      return { value, label: String(value) };
    })
    .filter(Boolean)
);

const JOBS_PAGE_SIZE = 200;

const isJobSelectable = (job) =>
  job && !job.isDeleting && job.isEnabled !== false;

const JobPlannerPage = ({ embedded = false, onSelectJob, onEmbeddedHeaderActions }) => {
  const { currentUser } = useAuth();
  const { confirm } = useConfirm();
  const dispatch = useDispatch();
  const translation = useTranslation();
  const dashboardJobs = useSelector((state) => state.dashboard?.jobs ?? []);
  const currentCompany = useSelector((state) => state.dashboard?.currentCompany);

  useEffect(() => {
    if (!embedded) {
      dispatch(setPageTitle("Job Planner"));
    }
  }, [dispatch, embedded]);

  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [allClients, setAllClients] = useState([]);
  const [allFleets, setAllFleets] = useState([]);
  const [allPads, setAllPads] = useState([]);
  const [allWells, setAllWells] = useState([]);

  const [newRows, setNewRows] = useState([]);
  const [editsById, setEditsById] = useState({});

  const [saving, setSaving] = useState(false);

  const inlineFileInputRef = useRef(null);
  const inlineFileUploadRowIdRef = useRef(null);
  const padMenuHoverTimerRef = useRef(null);

  const [clientPopover, setClientPopover] = useState({ anchorEl: null, rowId: null });
  const [fleetPopover, setFleetPopover] = useState({ anchorEl: null, rowId: null });
  const [padPopover, setPadPopover] = useState({ anchorEl: null, rowId: null });
  const [hoveredPadInMenu, setHoveredPadInMenu] = useState(null);
  const [hoveredPadMenuTop, setHoveredPadMenuTop] = useState(0);
  const padMenuContainerRef = useRef(null);
  const [expandedRowId, setExpandedRowId] = useState(null);
  const [jobsByPlannerId, setJobsByPlannerId] = useState({});
  const [jobsLoadingByPlannerId, setJobsLoadingByPlannerId] = useState({});
  const [jobsSearchTerm, setJobsSearchTerm] = useState("");
  const [jobsSelectedLicenses, setJobsSelectedLicenses] = useState([]);
  const [jobsSelectedStages, setJobsSelectedStages] = useState([]);
  const [filterLicenseOptions, setFilterLicenseOptions] = useState([]);
  const [filterStageOptions, setFilterStageOptions] = useState([]);
  const [jobsFilterOptionsLoading, setJobsFilterOptionsLoading] = useState(false);
  const [jobsFilterAnchor, setJobsFilterAnchor] = useState({ key: null });
  const [jobsFilterPopoverEl, setJobsFilterPopoverEl] = useState(null);
  const [jobsFilterSearchTerm, setJobsFilterSearchTerm] = useState("");
  const [pendingJobsFilterValue, setPendingJobsFilterValue] = useState([]);
  const jobsSelectedLicensesRef = useRef(jobsSelectedLicenses);
  const jobsSelectedStagesRef = useRef(jobsSelectedStages);
  const [selectingJobId, setSelectingJobId] = useState(null);
  const [licensesNames, setLicensesNames] = useState(null);
  const companyId = currentUser?.isSuperuser?.() ? currentCompany?.id : undefined;

  const fetchRecords = useCallback(async () => {
    setLoading(true);
    try {
      const res = await DmsService.getJobsPlanner(1, 200, companyId);
      setRecords(normalizeCatalogResponse(res.data));
      setNewRows([]);
      setEditsById({});
    } catch {
      toast.error("Failed to load job planners");
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    if (currentUser?.isSuperuser?.() && !currentCompany) return;
    fetchRecords();
  }, [fetchRecords, currentUser, currentCompany]);

  useEffect(
    () => () => {
      if (padMenuHoverTimerRef.current) {
        clearTimeout(padMenuHoverTimerRef.current);
      }
    },
    [],
  );

  useEffect(() => {
    if (currentUser?.isSuperuser?.() && !currentCompany) return;

    const fetchLicenses = async () => {
      try {
        const response = currentUser?.isSuperuser?.()
          ? await DmsService.getLicenseIdsByCompanyId(currentCompany.id)
          : await DmsService.getLicenseIds();
        setLicensesNames(response.data);
      } catch {
        setLicensesNames(null);
      }
    };

    fetchLicenses();
  }, [companyId, currentUser, currentCompany]);

  useEffect(() => {
    if (currentUser?.isSuperuser?.() && !currentCompany) return;
    DmsService.getCatalog("clients", 1, 500, companyId)
      .then((res) => setAllClients(normalizeCatalogResponse(res.data)))
      .catch(() => setAllClients([]));
    DmsService.getCatalog("fleets", 1, 500, companyId)
      .then((res) => setAllFleets(normalizeCatalogResponse(res.data)))
      .catch(() => setAllFleets([]));
    DmsService.getCatalog("pads", 1, 500, companyId)
      .then((res) => setAllPads(normalizeCatalogResponse(res.data)))
      .catch(() => setAllPads([]));
    DmsService.getCatalog("wells", 1, 1000, companyId)
      .then((res) => setAllWells(normalizeCatalogResponse(res.data)))
      .catch(() => setAllWells([]));
  }, [companyId, currentUser, currentCompany]);

  const getDisplayRecord = useCallback(
    (record) => {
      const edits = editsById[record.id];
      if (!edits) return record;
      return {
        ...record,
        ...edits,
      };
    },
    [editsById],
  );

  const displayRecords = useMemo(
    () => records.map((record) => getDisplayRecord(record)),
    [records, getDisplayRecord],
  );

  const tableRows = useMemo(
    () => [...newRows, ...displayRecords],
    [newRows, displayRecords],
  );

  const hasPendingChanges = useMemo(
    () => newRows.length > 0 || Object.keys(editsById).length > 0,
    [newRows, editsById],
  );

  const isRowFieldChanged = useCallback(
    (row, fieldKey) => {
      const isNew = String(row.id).startsWith("new-");
      if (isNew) {
        if (fieldKey === "clientId") return !!row.clientId;
        if (fieldKey === "fleetId") return !!row.fleetId;
        if (fieldKey === "padId") return !!row.padId;
        if (fieldKey === "wellId") return !!row.wellId;
        if (fieldKey === "files") {
          return (
            parseRowFilesJson(row.filesJson).length > 0 ||
            (row.pendingFiles || []).length > 0
          );
        }
        return false;
      }

      const original = records.find((r) => r.id === row.id);
      if (!original || !editsById[row.id]) return false;
      const edits = editsById[row.id];

      if (fieldKey === "clientId") {
        return edits.clientId !== undefined && edits.clientId !== (original.clientId || "");
      }
      if (fieldKey === "fleetId") {
        return edits.fleetId !== undefined && edits.fleetId !== (original.fleetId || "");
      }
      if (fieldKey === "padId") {
        return edits.padId !== undefined && edits.padId !== (original.padId || "");
      }
      if (fieldKey === "wellId") {
        return edits.wellId !== undefined && edits.wellId !== (original.wellId || "");
      }
      if (fieldKey === "files") {
        const origFiles = parseRowFilesJson(original.filesJson);
        const currentFiles = parseRowFilesJson(row.filesJson);
        const hasPending = (row.pendingFiles || []).length > 0;
        return hasPending || JSON.stringify(currentFiles) !== JSON.stringify(origFiles);
      }
      return false;
    },
    [records, editsById],
  );

  const getCellBorderClass = useCallback(
    (row, fieldKey) => {
      if (!isRowFieldChanged(row, fieldKey)) return "";
      return saving ? "jobPlannerCellSaving" : "jobPlannerCellChanged";
    },
    [isRowFieldChanged, saving],
  );

  const wellsForPad = useCallback(
    (padId) => allWells.filter((w) => String(w.padId) === String(padId)),
    [allWells],
  );

  const updateInlineEdit = useCallback((recordId, patch) => {
    setEditsById((prev) => {
      const current = prev[recordId] || {};
      const next = { ...current, ...patch };
      const original = records.find((r) => r.id === recordId);
      if (!original) return prev;

      const merged = {
        ...original,
        ...next,
      };
      const unchanged =
        (merged.clientId || "") === (original.clientId || "") &&
        (merged.fleetId || "") === (original.fleetId || "") &&
        merged.padId === original.padId &&
        (merged.wellId || "") === (original.wellId || "") &&
        JSON.stringify(parseRowFilesJson(merged.filesJson)) ===
          JSON.stringify(parseRowFilesJson(original.filesJson)) &&
        (merged.pendingFiles || []).length === 0;

      if (unchanged) {
        const { [recordId]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [recordId]: next };
    });
  }, [records]);

  const updateNewRow = useCallback((rowId, patch) => {
    setNewRows((prev) =>
      prev.map((row) => (row.id === rowId ? { ...row, ...patch } : row)),
    );
  }, []);

  const applyRowFieldChange = useCallback(
    (rowId, patch) => {
      if (String(rowId).startsWith("new-")) {
        updateNewRow(rowId, patch);
      } else {
        updateInlineEdit(rowId, patch);
      }
    },
    [updateNewRow, updateInlineEdit],
  );

  const openClientPopover = useCallback((event, rowId) => {
    event.stopPropagation();
    setClientPopover({ anchorEl: event.currentTarget, rowId });
  }, []);

  const closeClientPopover = useCallback(() => {
    setClientPopover({ anchorEl: null, rowId: null });
  }, []);

  const openFleetPopover = useCallback((event, rowId) => {
    event.stopPropagation();
    setFleetPopover({ anchorEl: event.currentTarget, rowId });
  }, []);

  const closeFleetPopover = useCallback(() => {
    setFleetPopover({ anchorEl: null, rowId: null });
  }, []);

  const openPadPopover = useCallback((event, rowId) => {
    event.stopPropagation();
    setPadPopover({ anchorEl: event.currentTarget, rowId });
  }, []);

  const closePadPopover = useCallback(() => {
    if (padMenuHoverTimerRef.current) {
      clearTimeout(padMenuHoverTimerRef.current);
      padMenuHoverTimerRef.current = null;
    }
    setPadPopover({ anchorEl: null, rowId: null });
    setHoveredPadInMenu(null);
    setHoveredPadMenuTop(0);
  }, []);

  const clearPadMenuHoverTimer = useCallback(() => {
    if (padMenuHoverTimerRef.current) {
      clearTimeout(padMenuHoverTimerRef.current);
      padMenuHoverTimerRef.current = null;
    }
  }, []);

  const scheduleClearHoveredPadInMenu = useCallback(() => {
    clearPadMenuHoverTimer();
    padMenuHoverTimerRef.current = setTimeout(() => {
      setHoveredPadInMenu(null);
      setHoveredPadMenuTop(0);
    }, 150);
  }, [clearPadMenuHoverTimer]);

  const handleCancelPending = useCallback(() => {
    setNewRows([]);
    setEditsById({});
    closeClientPopover();
    closeFleetPopover();
    closePadPopover();
  }, [closeClientPopover, closeFleetPopover, closePadPopover]);

  const selectClient = useCallback(
    (client) => {
      const { rowId } = clientPopover;
      if (rowId == null) {
        closeClientPopover();
        return;
      }
      applyRowFieldChange(rowId, { clientId: client?.id || "" });
      closeClientPopover();
    },
    [clientPopover, applyRowFieldChange, closeClientPopover],
  );

  const selectFleet = useCallback(
    (fleet) => {
      const { rowId } = fleetPopover;
      if (rowId == null) {
        closeFleetPopover();
        return;
      }
      applyRowFieldChange(rowId, { fleetId: fleet?.id || "" });
      closeFleetPopover();
    },
    [fleetPopover, applyRowFieldChange, closeFleetPopover],
  );

  const selectPad = useCallback(
    (pad) => {
      const { rowId } = padPopover;
      if (rowId == null) {
        closePadPopover();
        return;
      }
      applyRowFieldChange(rowId, { padId: pad?.id || "", wellId: "" });
      closePadPopover();
    },
    [padPopover, applyRowFieldChange, closePadPopover],
  );

  const selectPadAndWell = useCallback(
    (pad, well) => {
      const { rowId } = padPopover;
      if (rowId == null) {
        closePadPopover();
        return;
      }
      applyRowFieldChange(rowId, {
        padId: pad?.id || "",
        wellId: well?.id || "",
      });
      closePadPopover();
    },
    [padPopover, applyRowFieldChange, closePadPopover],
  );

  const handleInlineAdd = () => {
    setNewRows((prev) => [createInlineRow(), ...prev]);
  };

  const handleRemoveNewRow = (rowId) => {
    setNewRows((prev) => prev.filter((row) => row.id !== rowId));
  };

  const validateRowsForUpsert = (rows) => {
    for (const row of rows) {
      if (!row.padId) {
        toast.error("Pad is required");
        return false;
      }
      // if (!row.wellId) {
      //   toast.error("Well is required");
      //   return false;
      // }
    }
    return true;
  };

  const collectPendingRows = useCallback(() => {
    const rows = [];

    newRows.forEach((row) => {
      if (!isInlineRowComplete(row)) return;
      rows.push({
        clientId: row.clientId,
        fleetId: row.fleetId,
        padId: row.padId,
        wellId: row.wellId,
        parametersJson: null,
        filesJson: row.filesJson ?? null,
        pendingFiles: row.pendingFiles || [],
      });
    });

    Object.keys(editsById).forEach((id) => {
      const original = records.find((r) => r.id === id);
      if (!original) return;
      const display = getDisplayRecord(original);
      rows.push({
        id: display.id,
        name: display.name || "",
        clientId: display.clientId,
        fleetId: display.fleetId,
        padId: display.padId,
        wellId: display.wellId,
        parametersJson: display.parametersJson ?? null,
        filesJson: display.filesJson ?? null,
        pendingFiles: display.pendingFiles || [],
      });
    });

    return rows;
  }, [newRows, editsById, records, getDisplayRecord]);

  const prepareRowsWithUploadedFiles = useCallback(
    async (rows) => {
      return Promise.all(
        rows.map(async (row) => {
          const uploaded = parseRowFilesJson(row.filesJson);
          const pending = row.pendingFiles || [];
          if (pending.length === 0) {
            return {
              ...row,
              filesJson: uploaded.length > 0 ? JSON.stringify(uploaded) : null,
            };
          }
          const newlyUploaded = await Promise.all(
            pending.map((file) =>
              DmsService.uploadJobPlannerFile(file, companyId).then((res) => ({
                url: resolveFileUrl(res.data.url),
                name: res.data.fileName,
              })),
            ),
          );
          const allFiles = [...uploaded, ...newlyUploaded];
          return {
            ...row,
            filesJson: allFiles.length > 0 ? JSON.stringify(allFiles) : null,
            pendingFiles: [],
          };
        }),
      );
    },
    [companyId],
  );

  const upsertRows = useCallback(
    async (rows, { successMessage = "Job planner saved" } = {}) => {
      if (!rows.length) return;
      if (!validateRowsForUpsert(rows)) return false;

      const payload = rows.map((row) => buildUpsertPayload(row, allPads, allWells));
      await DmsService.upsertJobsPlanner(payload, companyId);
      toast.success(successMessage);
      return true;
    },
    [allPads, allWells, companyId],
  );

  const validateIncompleteNewRows = () => {
    const incomplete = newRows.filter(
      (row) => hasInlineRowData(row) && !isInlineRowComplete(row),
    );
    if (incomplete.length === 0) return true;
    toast.error("Pad is required");
    return false;
  };

  const handleSavePending = async () => {
    if (!validateIncompleteNewRows()) return;

    const rows = collectPendingRows();
    if (!rows.length) return;

    setSaving(true);
    try {
      const rowsWithFiles = await prepareRowsWithUploadedFiles(rows);
      const ok = await upsertRows(rowsWithFiles, { successMessage: "Job planners saved" });
      if (ok) {
        await fetchRecords();
      }
    } catch (err) {
      toast.error(err?.response?.data?.message ?? "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const embeddedHeaderActionsRef = useRef(null);
  embeddedHeaderActionsRef.current = {
    hasPendingChanges,
    saving,
    onAdd: handleInlineAdd,
    onCancel: handleCancelPending,
    onSave: handleSavePending,
    addLabel: translation.addButtonLabel,
  };

  useEffect(() => {
    if (!embedded || !onEmbeddedHeaderActions) return undefined;

    onEmbeddedHeaderActions({ ...embeddedHeaderActionsRef.current });

    return () => onEmbeddedHeaderActions(null);
  }, [
    embedded,
    onEmbeddedHeaderActions,
    hasPendingChanges,
    saving,
    translation.addButtonLabel,
  ]);

  const handleDelete = async (id) => {
    const isConfirmed = await confirm("Delete this job planner?");
    if (!isConfirmed) return;

    try {
      await DmsService.deleteJobsPlanner([id], companyId);
      setRecords((prev) => prev.filter((r) => r.id !== id));
      setExpandedRowId((prev) => (prev === id ? null : prev));
      setEditsById((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
      toast.success("Deleted");
    } catch (err) {
      toast.error(err?.response?.data?.message ?? "Failed to delete");
    }
  };

  const getClientName = (clientId) => {
    if (!clientId) return "—";
    const client = allClients.find((c) => c.id === clientId);
    return client ? client.name : "—";
  };

  const getFleetName = (fleetId) => {
    if (!fleetId) return "—";
    const fleet = allFleets.find((f) => f.id === fleetId);
    return fleet ? fleet.name : "—";
  };

  const formatPadCellLabel = useCallback(
    (row) => {
      if (!row.padId) return "Select pad";
      return formatPadWellSelectionLabel(row.padId, row.wellId, allPads, allWells);
    },
    [allPads, allWells],
  );

  const getPadCellBorderClass = useCallback(
    (row) => [getCellBorderClass(row, "padId"), getCellBorderClass(row, "wellId")]
      .filter(Boolean)
      .join(" "),
    [getCellBorderClass],
  );

  const triggerInlineFileInput = (rowId) => {
    inlineFileUploadRowIdRef.current = rowId;
    inlineFileInputRef.current?.click();
  };

  const handleInlineFileInputChange = (e) => {
    const rowId = inlineFileUploadRowIdRef.current;
    const selectedFiles = Array.from(e.target.files || []);
    e.target.value = "";
    if (!rowId || selectedFiles.length === 0) return;

    const row = [...newRows, ...displayRecords].find((r) => r.id === rowId);
    if (!row) return;

    applyRowFieldChange(rowId, {
      pendingFiles: [...(row.pendingFiles || []), ...selectedFiles],
    });
  };

  const removeUploadedFile = (rowId, fileIndex) => {
    const row = [...newRows, ...displayRecords].find((r) => r.id === rowId);
    if (!row) return;
    const uploaded = parseRowFilesJson(row.filesJson);
    const next = uploaded.filter((_, i) => i !== fileIndex);
    applyRowFieldChange(rowId, {
      filesJson: next.length > 0 ? JSON.stringify(next) : null,
    });
  };

  const fetchPlannerJobs = useCallback(async (plannerId, licenseIds, searchTerm, selectedStages) => {
    setJobsLoadingByPlannerId((prev) => ({ ...prev, [plannerId]: true }));
    try {
      const data = await DmsService.getJobsShortPaged(
        0,
        JOBS_PAGE_SIZE,
        licenseIds,
        searchTerm,
        plannerId,
        selectedStages,
      );
      setJobsByPlannerId((prev) => ({ ...prev, [plannerId]: data || [] }));
    } catch {
      toast.error("Failed to load jobs");
      setJobsByPlannerId((prev) => ({ ...prev, [plannerId]: [] }));
    } finally {
      setJobsLoadingByPlannerId((prev) => ({ ...prev, [plannerId]: false }));
    }
  }, []);

  const handleRowClick = useCallback((row) => {
    if (String(row.id).startsWith("new-")) return;
    setExpandedRowId((prev) => (prev === row.id ? null : row.id));
  }, []);

  useEffect(() => {
    setJobsSearchTerm("");
    setJobsSelectedLicenses([]);
    setJobsSelectedStages([]);
    setFilterLicenseOptions([]);
    setFilterStageOptions([]);
    setJobsFilterOptionsLoading(false);
    setJobsFilterAnchor({ key: null });
    setJobsFilterPopoverEl(null);
    setJobsFilterSearchTerm("");
    setPendingJobsFilterValue([]);

    if (!expandedRowId) {
      setJobsByPlannerId({});
      setJobsLoadingByPlannerId({});
    }
  }, [expandedRowId]);

  jobsSelectedLicensesRef.current = jobsSelectedLicenses;
  jobsSelectedStagesRef.current = jobsSelectedStages;

  useEffect(() => {
    if (jobsFilterAnchor.key === "license") {
      setPendingJobsFilterValue([...jobsSelectedLicensesRef.current]);
    } else if (jobsFilterAnchor.key === "stage") {
      setPendingJobsFilterValue([...jobsSelectedStagesRef.current]);
    }
  }, [jobsFilterAnchor.key]);

  useEffect(() => {
    if (!expandedRowId || !jobsFilterAnchor.key) return undefined;

    let cancelled = false;
    setJobsFilterOptionsLoading(true);

    const fetchFilterOptions = async () => {
      try {
        if (jobsFilterAnchor.key === "license") {
          const data = await DmsService.getJobsPlannerFilterLicenses(expandedRowId);
          if (!cancelled) {
            setFilterLicenseOptions(normalizeLicenseFilterOptions(data, licensesNames));
          }
        } else if (jobsFilterAnchor.key === "stage") {
          const data = await DmsService.getJobsPlannerFilterStages(expandedRowId);
          if (!cancelled) {
            setFilterStageOptions(normalizeStageFilterOptions(data));
          }
        }
      } catch {
        if (!cancelled) {
          if (jobsFilterAnchor.key === "license") {
            setFilterLicenseOptions([]);
          } else if (jobsFilterAnchor.key === "stage") {
            setFilterStageOptions([]);
          }
        }
      } finally {
        if (!cancelled) {
          setJobsFilterOptionsLoading(false);
        }
      }
    };

    fetchFilterOptions();

    return () => {
      cancelled = true;
    };
  }, [expandedRowId, jobsFilterAnchor.key, licensesNames]);

  useEffect(() => {
    if (!expandedRowId) return undefined;

    setJobsLoadingByPlannerId((prev) => ({ ...prev, [expandedRowId]: true }));
    setJobsByPlannerId((prev) => ({ ...prev, [expandedRowId]: [] }));

    const debouncedFetch = debounce(() => {
      fetchPlannerJobs(
        expandedRowId,
        jobsSelectedLicenses,
        jobsSearchTerm,
        jobsSelectedStages,
      );
    }, 300);

    debouncedFetch();

    return () => {
      debouncedFetch.cancel();
    };
  }, [
    expandedRowId,
    jobsSelectedLicenses,
    jobsSelectedStages,
    jobsSearchTerm,
    fetchPlannerJobs,
  ]);

  const jobsFilterOptions = useMemo(() => {
    if (jobsFilterAnchor.key === "license") {
      return filterLicenseOptions;
    }
    if (jobsFilterAnchor.key === "stage") {
      return filterStageOptions;
    }
    return [];
  }, [jobsFilterAnchor.key, filterLicenseOptions, filterStageOptions]);

  const handleJobsFilterClick = useCallback((key, event) => {
    event.stopPropagation();
    if (jobsFilterAnchor.key === key) {
      setJobsFilterAnchor({ key: null });
      setJobsFilterPopoverEl(null);
    } else {
      setJobsFilterAnchor({ key });
      setJobsFilterPopoverEl(
        event.currentTarget.closest(".jobPlannerInnerJobsHeaderFilterBtn") || event.currentTarget,
      );
    }
    setJobsFilterSearchTerm("");
  }, [jobsFilterAnchor.key]);

  const handleCloseJobsFilter = useCallback(() => {
    setJobsFilterAnchor({ key: null });
    setJobsFilterPopoverEl(null);
    setJobsFilterSearchTerm("");
  }, []);

  const togglePendingJobsFilterOption = useCallback((optionValue) => {
    setPendingJobsFilterValue((prev) => (
      prev.includes(optionValue)
        ? prev.filter((item) => item !== optionValue)
        : [...prev, optionValue]
    ));
  }, []);

  const handleApplyJobsFilter = useCallback(() => {
    if (jobsFilterAnchor.key === "license") {
      setJobsSelectedLicenses(pendingJobsFilterValue);
    } else if (jobsFilterAnchor.key === "stage") {
      setJobsSelectedStages(pendingJobsFilterValue);
    }
    handleCloseJobsFilter();
  }, [jobsFilterAnchor.key, pendingJobsFilterValue, handleCloseJobsFilter]);

  const handleClearJobsFilter = useCallback(() => {
    if (jobsFilterAnchor.key === "license") {
      setJobsSelectedLicenses([]);
    } else if (jobsFilterAnchor.key === "stage") {
      setJobsSelectedStages([]);
    }
    setPendingJobsFilterValue([]);
    handleCloseJobsFilter();
  }, [jobsFilterAnchor.key, handleCloseJobsFilter]);

  const filteredJobsFilterOptions = useMemo(() => {
    const search = jobsFilterSearchTerm.trim().toLowerCase();
    if (!search) return jobsFilterOptions;

    return jobsFilterOptions.filter((option) => {
      const hay = `${option.value} ${option.label}`.toLowerCase();
      return hay.includes(search);
    });
  }, [jobsFilterOptions, jobsFilterSearchTerm]);

  const renderJobsHeaderFilterIcon = (filterKey, label, selectedCount) => (
    <IconButton
      size="small"
      className={`jobPlannerInnerJobsHeaderFilterBtn ${jobsFilterAnchor.key === filterKey ? "isOpen" : ""}`.trim()}
      onClick={(event) => handleJobsFilterClick(filterKey, event)}
      aria-label={`Filter ${label}`}
    >
      <Badge
        badgeContent={selectedCount > 0 ? selectedCount : null}
        color="primary"
        overlap="rectangular"
      >
        <FilterListIcon fontSize="small" />
      </Badge>
    </IconButton>
  );

  const removePendingFile = (rowId, fileIndex) => {
    const row = [...newRows, ...displayRecords].find((r) => r.id === rowId);
    if (!row) return;
    const next = (row.pendingFiles || []).filter((_, i) => i !== fileIndex);
    applyRowFieldChange(rowId, { pendingFiles: next });
  };

  const renderInlineFilesCell = (row) => {
    const uploaded = parseRowFilesJson(row.filesJson);
    const pending = row.pendingFiles || [];
    const hasFiles = uploaded.length > 0 || pending.length > 0;
    const filesCellClass = getCellBorderClass(row, "files");

    const openFilePicker = (e) => {
      e.stopPropagation();
      triggerInlineFileInput(row.id);
    };

    const renderFileItem = (file, idx, { isPending }) => (
      <span
        key={isPending ? `pending-${idx}` : `uploaded-${idx}`}
        className="jobPlannerFileChip"
        title={file.name}
      >
        {isPending ? (
          <span className="jobPlannerFileChipLabel">{file.name}</span>
        ) : (
          <a
            href={resolveFileUrl(file.url)}
            className="jobPlannerFileChipLabel jobPlannerFileChipLink"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              downloadFileFromUrl(file.url, file.name);
            }}
          >
            {file.name}
          </a>
        )}
        <IconButton
          size="small"
          className="jobPlannerFileChipRemove"
          onClick={(e) => {
            e.stopPropagation();
            if (isPending) {
              removePendingFile(row.id, idx);
            } else {
              removeUploadedFile(row.id, idx);
            }
          }}
          aria-label="Remove file"
        >
          <span className="removeIcon" />
        </IconButton>
      </span>
    );

    if (!hasFiles) {
      return (
        <span
          role="button"
          tabIndex={0}
          className={`jobPlannerInlineFileAddTrigger ${filesCellClass}`.trim()}
          onClick={openFilePicker}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              openFilePicker(e);
            }
          }}
        >
          <span className="amPlIcon" aria-hidden />
          Add file
        </span>
      );
    }

    return (
      <div
        className={`jobPlannerInlineFiles jobPlannerInlineFilesHasItems ${filesCellClass}`.trim()}
      >
        <div className="jobPlannerInlineFileList">
          {uploaded.map((file, idx) => renderFileItem(file, idx, { isPending: false }))}
          {pending.map((file, idx) => renderFileItem(file, idx, { isPending: true }))}
        </div>
        <IconButton
          size="small"
          className="jobPlannerInlineFileAddBtn"
          onClick={openFilePicker}
          aria-label="Add file"
        >
          <span className="amPlIcon" aria-hidden />
        </IconButton>
      </div>
    );
  };

  const renderPadCell = (row) => {
    const label = formatPadCellLabel(row);
    const borderClass = getPadCellBorderClass(row);

    return (
      <span
        role="button"
        tabIndex={0}
        className={`${JOB_PLANNER_CELL_DISPLAY_CLASS} ${borderClass}`.trim()}
        title={label}
        onClick={(event) => {
          event.stopPropagation();
          openPadPopover(event, row.id);
        }}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            openPadPopover(event, row.id);
          }
        }}
      >
        {label}
      </span>
    );
  };

  const renderPopoverCell = (row, fieldKey, displayText, onOpen) => (
    <span
      role="button"
      tabIndex={0}
      className={`${JOB_PLANNER_CELL_DISPLAY_CLASS} ${getCellBorderClass(row, fieldKey)}`.trim()}
      onClick={(e) => {
        e.stopPropagation();
        onOpen(e);
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen(e);
        }
      }}
    >
      {displayText}
    </span>
  );

  const getLicenseName = useCallback(
    (job) =>
      licensesNames?.find((ln) => ln.licenseId === job.licenseId)?.name ||
      job.licenseId ||
      "—",
    [licensesNames],
  );

  const formatJobDate = (value) =>
    value ? moment(value).format("DD-MMM-YY, HH:mm") : "—";

  const handlePlannerJobClick = useCallback(
    async (job) => {
      if (!isJobSelectable(job) || selectingJobId != null) return;

      setSelectingJobId(job.id);
      try {
        const jobFromList = dashboardJobs.find((item) => item.id === job.id) || job;
        const response = await DmsService.getJobsById(job.id);
        const jobToSelect = {
          ...jobFromList,
          ...response,
          units: response?.units,
        };
        if (onSelectJob) {
          onSelectJob(jobToSelect);
        } else {
          setPendingMainDashboardJob({ job: jobToSelect, company: currentCompany });
          window.open(`${window.location.origin}/`, "_blank");
        }
      } catch {
        toast.error("Failed to load job");
      } finally {
        setSelectingJobId(null);
      }
    },
    [currentCompany, dashboardJobs, onSelectJob, selectingJobId],
  );

  const renderPlannerJobsTable = (row) => {
    const plannerId = row.id;
    const jobs = jobsByPlannerId[plannerId] || [];
    const isJobsLoading = jobsLoadingByPlannerId[plannerId];

    return (
      <div className="jobPlannerInnerJobsPanel">
        <div className="jobPlannerInnerJobsFilters">
          <TextField
            variant="outlined"
            margin="dense"
            size="small"
            className="jobPlannerInnerJobsFilterField"
            name="search"
            label={translation.dashboardSearchJobLabel}
            type="text"
            value={jobsSearchTerm}
            onChange={(e) => setJobsSearchTerm(e.target.value)}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          />
        </div>
        <Popover
          open={Boolean(jobsFilterAnchor.key && jobsFilterPopoverEl)}
          anchorEl={jobsFilterPopoverEl}
          onClose={handleCloseJobsFilter}
          className="jobPlannerInnerJobsFilterPopover"
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "left",
          }}
          PaperProps={{
            className: "jobPlannerInnerJobsFilterPaper",
          }}
        >
          {jobsFilterAnchor.key && (
            <div className="jobPlannerInnerJobsFilterPopoverContent">
              <div className="jobPlannerInnerJobsFilterPopoverActions">
                <Button
                  size="small"
                  className="jobPlannerInnerJobsFilterClearBtn"
                  onClick={handleClearJobsFilter}
                  disabled={pendingJobsFilterValue.length === 0}
                >
                  Clear filter
                </Button>
                <Button
                  size="small"
                  className="jobPlannerInnerJobsFilterApplyBtn"
                  onClick={handleApplyJobsFilter}
                  disabled={pendingJobsFilterValue.length === 0}
                >
                  Apply
                </Button>
              </div>
              <TextField
                size="small"
                variant="outlined"
                placeholder="Search..."
                value={jobsFilterSearchTerm}
                onChange={(e) => setJobsFilterSearchTerm(e.target.value)}
                fullWidth
                className="jobPlannerInnerJobsFilterPopoverSearch"
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => e.stopPropagation()}
              />
              <div className="jobPlannerInnerJobsFilterListbox">
                {jobsFilterOptionsLoading ? (
                  <div className="jobPlannerInnerJobsFilterLoader">
                    <CircularProgress size={20} />
                  </div>
                ) : filteredJobsFilterOptions.length === 0 ? (
                  <div className="jobPlannerInnerJobsFilterEmpty">
                    {jobsFilterAnchor.key === "stage" ? "No stages" : "No licenses"}
                  </div>
                ) : (
                  filteredJobsFilterOptions.map((option) => (
                    <div
                      key={String(option.value)}
                      className="jobPlannerInnerJobsFilterOption"
                      onClick={() => togglePendingJobsFilterOption(option.value)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          togglePendingJobsFilterOption(option.value);
                        }
                      }}
                    >
                      <Checkbox
                        color="primary"
                        size="small"
                        checked={pendingJobsFilterValue.includes(option.value)}
                        onClick={(e) => {
                          e.stopPropagation();
                          togglePendingJobsFilterOption(option.value);
                        }}
                      />
                      <span className="jobPlannerInnerJobsFilterOptionLabel" title={option.label}>
                        {option.label}
                      </span>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </Popover>
        <div className="jobPlannerInnerJobsTableWrap">
          <table className="unitSettingsTable jobPlannerInnerJobsTable">
            <colgroup>
              <col className="jobPlannerNameCol" />
              <col className="jobPlannerPadCol" />
              <col className="jobPlannerWellCol" />
              <col className="jobPlannerCrewsCol" />
              <col className="jobPlannerStageCol" />
              <col className="actionTableCell" />
            </colgroup>
            <thead>
              <tr className="headingRow">
                <th className="jobPlannerNameCol">{translation.dashboardJobsLabel}</th>
                <th className="jobPlannerPadCol">
                  <span className="jobPlannerInnerJobsThLabel">
                    Licence
                    {renderJobsHeaderFilterIcon("license", "Licence", jobsSelectedLicenses.length)}
                  </span>
                </th>
                <th className="jobPlannerWellCol">{translation.dashboardStartTimeLabel}</th>
                <th className="jobPlannerCrewsCol">{translation.dashboardStopTimeLabel}</th>
                <th className="jobPlannerStageCol">
                  <span className="jobPlannerInnerJobsThLabel">
                    Stage
                    {renderJobsHeaderFilterIcon("stage", "Stage", jobsSelectedStages.length)}
                  </span>
                </th>
                <th className="actionTableCell" />
              </tr>
            </thead>
            <tbody>
              {isJobsLoading ? (
                <tr className="jobPlannerInnerJobsStateRow">
                  <td colSpan={6}>
                    <div className="jobPlannerInnerJobsLoader">
                      <CircularProgress size={14} />
                    </div>
                  </td>
                </tr>
              ) : jobs.length === 0 ? (
                <tr className="jobPlannerInnerJobsStateRow">
                  <td colSpan={6}>
                    <div className="jobPlannerInnerJobsEmpty">No jobs</div>
                  </td>
                </tr>
              ) : (
                jobs.map((job) => {
                  const selectable = isJobSelectable(job);
                  const isSelecting = selectingJobId === job.id;
                  const stageLabel = job.jobsPlannerStage || "";
                  return (
                    <tr
                      key={job.id}
                      className={[
                        selectable ? "jobPlannerInnerJobRowClickable" : "jobPlannerInnerJobRowDisabled",
                        isSelecting ? "jobPlannerInnerJobRowSelecting" : undefined,
                      ]
                        .filter(Boolean)
                        .join(" ")}
                      onClick={selectable && !isSelecting ? () => handlePlannerJobClick(job) : undefined}
                    >
                      <td className="jobPlannerNameCol jobPlannerInnerJobNameCell" title={job.name}>
                        {job.name}
                      </td>
                      <td className="jobPlannerPadCol" title={getLicenseName(job)}>
                        {getLicenseName(job)}
                      </td>
                      <td className="jobPlannerWellCol">{formatJobDate(job.startTime)}</td>
                      <td className="jobPlannerCrewsCol">{formatJobDate(job.stopTime)}</td>
                      <td className="jobPlannerStageCol" title={stageLabel}>
                        {stageLabel}
                      </td>
                      <td className="actionTableCell" />
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderTableRow = (row) => {
    const isNew = String(row.id).startsWith("new-");
    const isExpanded = expandedRowId === row.id;

    const clientLabel = row.clientId ? getClientName(row.clientId) : "Select client";
    const fleetLabel = row.fleetId ? getFleetName(row.fleetId) : "Select fleet";

    return (
      <Fragment key={row.id}>
        <tr
          className={[
            isNew ? "jobPlannerInlineNewRow" : undefined,
            isExpanded ? "jobPlannerRowExpanded" : undefined,
          ]
            .filter(Boolean)
            .join(" ") || undefined}
          onClick={isNew ? undefined : () => handleRowClick(row)}
        >
          <td className="jobPlannerNameCol">
            {!isNew && (
              <span
                className={`jobPlannerRowToggle ${isExpanded ? "expanded" : ""}`.trim()}
                aria-hidden
              />
            )}
            {buildJobName(row.padId, row.wellId, allPads, allWells) || row.name || "—"}
          </td>
          <td className="jobPlannerClientCol" onClick={(e) => e.stopPropagation()}>
            {renderPopoverCell(row, "clientId", clientLabel, (e) => openClientPopover(e, row.id))}
          </td>
          <td className="jobPlannerFleetCol" onClick={(e) => e.stopPropagation()}>
            {renderPopoverCell(row, "fleetId", fleetLabel, (e) => openFleetPopover(e, row.id))}
          </td>
          <td className="jobPlannerPadCol" onClick={(e) => e.stopPropagation()}>
            {renderPadCell(row)}
          </td>
          <td className="jobPlannerFilesCol" onClick={(e) => e.stopPropagation()}>
            {renderInlineFilesCell(row)}
          </td>
          <td className="actionTableCell" onClick={(e) => e.stopPropagation()}>
            <IconButton
              size="small"
              className="deleteTableItemBtn"
              onClick={() => (isNew ? handleRemoveNewRow(row.id) : handleDelete(row.id))}
              aria-label={isNew ? "Remove" : "Delete"}
            >
              <RemoveIcon />
            </IconButton>
          </td>
        </tr>
        {isExpanded && !isNew && (
          <tr className="jobPlannerInnerTableHolder">
            <td className="jobPlannerInnerTableContent" colSpan={6}>
              {renderPlannerJobsTable(row)}
            </td>
          </tr>
        )}
      </Fragment>
    );
  };

  return (
    <div className={`jobPlannerPage ${embedded ? "jobPlannerPageEmbedded" : ""}`.trim()}>
      <div className="pageCard jobPlannerTableCard jobPlannerTableCardWithLoader">
        {!embedded && (
          <div className="pageBtnSection pt0 mt5 unitSettingsPageToolbar jobPlannerToolbar">
            <div className="jobPlannerActions">
              {hasPendingChanges && (
                <>
                  <Button
                    variant="outlined"
                    className="jobPlannerCancelBtn"
                    onClick={handleCancelPending}
                    disabled={saving}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    className="jobPlannerSaveBtn"
                    onClick={handleSavePending}
                    disabled={saving}
                  >
                    {saving ? "Saving…" : "Save"}
                  </Button>
                </>
              )}
              <Button
                className="asideNavItem jobConfigurationAddBtn jobPlannerAddBtn"
                startIcon={<MyIcons icon="addCircleOutlineIcon" />}
                onClick={handleInlineAdd}
              >
                {translation.addButtonLabel}
              </Button>
            </div>
          </div>
        )}
        <div className="tableHolder" style={{ position: "relative", minHeight: 120 }}>
          <Fade in={loading} unmountOnExit>
            <div className="jobPlannerLoader">
              <CircularProgress size={32} />
            </div>
          </Fade>
          {!loading && (
            <table className="unitSettingsTable">
              <thead>
                <tr className="headingRow">
                  <th className="jobPlannerNameCol">Name</th>
                  <th className="jobPlannerClientCol">Client</th>
                  <th className="jobPlannerFleetCol">Fleet</th>
                  <th className="jobPlannerPadCol">Well</th>
                  <th className="jobPlannerFilesCol">Files</th>
                  <th className="actionTableCell" />
                </tr>
              </thead>
              <tbody>
                {tableRows.length === 0 ? (
                  <tr>
                  <td colSpan={6} style={{ borderBottom: "none" }}>
                      
                    </td>
                  </tr>
                ) : (
                  tableRows.map((row) => renderTableRow(row))
                )}
              </tbody>
            </table>
          )}
        </div>
        <input
          type="file"
          hidden
          multiple
          ref={inlineFileInputRef}
          onChange={handleInlineFileInputChange}
        />
      </div>

      <Popover
        open={Boolean(clientPopover.anchorEl)}
        anchorEl={clientPopover.anchorEl}
        onClose={closeClientPopover}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{
          className: "jobPlannerCellPopover",
          style: clientPopover.anchorEl
            ? { width: clientPopover.anchorEl.offsetWidth, minWidth: clientPopover.anchorEl.offsetWidth }
            : undefined,
        }}
      >
        <List dense disablePadding>
          <ListItem button onClick={() => selectClient(null)}>
            <ListItemText primary="Select client" />
          </ListItem>
          {allClients.map((client) => (
            <ListItem key={client.id} button onClick={() => selectClient(client)}>
              <ListItemText primary={client.name} />
            </ListItem>
          ))}
        </List>
      </Popover>

      <Popover
        open={Boolean(fleetPopover.anchorEl)}
        anchorEl={fleetPopover.anchorEl}
        onClose={closeFleetPopover}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{
          className: "jobPlannerCellPopover",
          style: fleetPopover.anchorEl
            ? { width: fleetPopover.anchorEl.offsetWidth, minWidth: fleetPopover.anchorEl.offsetWidth }
            : undefined,
        }}
      >
        <List dense disablePadding>
          <ListItem button onClick={() => selectFleet(null)}>
            <ListItemText primary="Select fleet" />
          </ListItem>
          {allFleets.map((fleet) => (
            <ListItem key={fleet.id} button onClick={() => selectFleet(fleet)}>
              <ListItemText primary={fleet.name} />
            </ListItem>
          ))}
        </List>
      </Popover>

      <Popover
        open={Boolean(padPopover.anchorEl)}
        anchorEl={padPopover.anchorEl}
        onClose={closePadPopover}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{
          className: "jobPlannerCellPopover jobPlannerPadMenuPopover",
          style: padPopover.anchorEl
            ? {
                width: padPopover.anchorEl.offsetWidth,
                minWidth: padPopover.anchorEl.offsetWidth,
              }
            : undefined,
        }}
      >
        <div className="jobPlannerPadMenuContainer" ref={padMenuContainerRef}>
          <List
            dense
            disablePadding
            className="jobPlannerPadMenuList"
            onMouseLeave={scheduleClearHoveredPadInMenu}
          >
            <ListItem button onClick={() => selectPad(null)}>
              <ListItemText primary="Select pad" />
            </ListItem>
            {allPads.map((pad) => {
              const padWells = wellsForPad(pad.id);
              const hasWells = padWells.length > 0;
              const isHovered = hoveredPadInMenu === pad.id;

              return (
                <ListItem
                  key={pad.id}
                  button
                  className={[
                    hasWells ? "jobPlannerPadMenuItemWithWells" : undefined,
                    isHovered ? "jobPlannerPadMenuItemHovered" : undefined,
                  ]
                    .filter(Boolean)
                    .join(" ") || undefined}
                  onMouseEnter={(event) => {
                    clearPadMenuHoverTimer();
                    if (!hasWells) {
                      setHoveredPadInMenu(null);
                      setHoveredPadMenuTop(0);
                      return;
                    }
                    const container = padMenuContainerRef.current;
                    if (container) {
                      const containerTop = container.getBoundingClientRect().top;
                      const itemTop = event.currentTarget.getBoundingClientRect().top;
                      setHoveredPadMenuTop(itemTop - containerTop);
                    }
                    setHoveredPadInMenu(pad.id);
                  }}
                  onClick={() => selectPad(pad)}
                >
                  <ListItemText primary={pad.name} />
                  {hasWells && <span className="jobPlannerPadMenuArrow" aria-hidden />}
                </ListItem>
              );
            })}
          </List>
          {hoveredPadInMenu && wellsForPad(hoveredPadInMenu).length > 0 && (
            <div
              className="jobPlannerPadWellSubmenu"
              style={{ top: hoveredPadMenuTop }}
              onMouseEnter={clearPadMenuHoverTimer}
              onMouseLeave={scheduleClearHoveredPadInMenu}
            >
              <List dense disablePadding>
                {wellsForPad(hoveredPadInMenu).map((well) => {
                  const pad = allPads.find((item) => item.id === hoveredPadInMenu);
                  return (
                    <ListItem
                      key={well.id}
                      button
                      onClick={() => selectPadAndWell(pad, well)}
                    >
                      <ListItemText
                        primary={`${well.name}${formatWellStagesInParens(well)}`}
                      />
                    </ListItem>
                  );
                })}
              </List>
            </div>
          )}
        </div>
      </Popover>
    </div>
  );
};

export default JobPlannerPage;
